import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Radio, X } from "lucide-react";
import { Button } from "../../components/ui/button/index.js";
import { SimpleTooltip } from "../../components/ui/tooltip/index.js";
import { SheetDescription, SheetTitle } from "../../components/ui/sheet/index.js";
import { ChannelMark } from "../../components/channel/ChannelMark.js";
import { cn } from "../../lib/utils.js";
import { WorkspaceInternalSidebarHeader } from "../../components/layout/WorkspaceInternalSidebarHeader.js";
import {
  workspaceInternalNavItemClass,
  workspaceInternalNavLabelClass,
} from "../../components/layout/workspace-internal-sidebar.js";
import {
  contactSheetDescriptionClass,
  contactSheetHeaderClass,
  contactSheetHeaderIconClass,
  contactSheetTitleClass,
} from "../contacts/contact-sheet-presentation.js";

function drawerNavItemClass(selected: boolean): string {
  return cn(
    "group flex min-h-11 w-full items-center gap-3 rounded-lg border-s-2 px-3 py-2.5 text-start no-underline transition-colors duration-150 motion-reduce:transition-none",
    "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent/40",
    selected
      ? "border-accent bg-surface-selected font-semibold text-foreground"
      : "border-transparent font-medium text-foreground/80 hover:bg-surface-hover",
  );
}
import {
  channelStatusLabelKey,
  isChannelSidebarVisible,
  type ChannelPlatformId,
} from "./channel-connection-policy.js";
import { useChannelDirectory } from "./ChannelsWorkspaceLayout.js";

const PLATFORM_TYPE = {
  whatsapp: "WHATSAPP",
  facebook: "FACEBOOK",
  instagram: "INSTAGRAM",
} as const;

export function ChannelsSidebar({
  collapsed,
  onCollapsedChange,
  onNavigate,
  onClose,
  variant = "sidebar",
}: {
  collapsed: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  onNavigate?: () => void;
  onClose?: () => void;
  variant?: "sidebar" | "drawer";
}): React.JSX.Element {
  const { t } = useTranslation();
  const directory = useChannelDirectory();
  const isDrawer = variant === "drawer";
  const showLabels = isDrawer || !collapsed;

  const rows: Array<{ id: ChannelPlatformId; state: string }> = [];
  if (directory.whatsapp && isChannelSidebarVisible(directory.whatsapp.state)) {
    rows.push({ id: "whatsapp", state: directory.whatsapp.state });
  }
  if (directory.facebook && isChannelSidebarVisible(directory.facebook.state)) {
    rows.push({ id: "facebook", state: directory.facebook.state });
  }
  if (directory.instagram && isChannelSidebarVisible(directory.instagram.state)) {
    rows.push({ id: "instagram", state: directory.instagram.state });
  }

  return (
    <div
      className="flex h-full min-h-0 w-full flex-col bg-surface"
      data-testid="channels-sidebar"
      data-collapsed={collapsed && !isDrawer ? "true" : "false"}
    >
      {isDrawer ? (
        <header className={contactSheetHeaderClass}>
          <div className="flex min-w-0 items-start gap-3">
            <span className={contactSheetHeaderIconClass} aria-hidden="true">
              <Radio size={16} />
            </span>
            <div className="grid min-w-0 gap-1">
              <SheetTitle
                className={cn(
                  contactSheetTitleClass,
                  "text-[1.0625rem] leading-tight tracking-[-0.02em]",
                )}
              >
                {t("channels.nav.label")}
              </SheetTitle>
              <SheetDescription className={contactSheetDescriptionClass}>
                {t("channels.nav.drawerDescription")}
              </SheetDescription>
            </div>
          </div>
          {onClose ? (
            <Button
              type="button"
              variant="ghost"
              className="size-8 shrink-0 p-0 text-muted hover:text-foreground"
              onClick={onClose}
              aria-label={t("common.close")}
            >
              <X className="size-4" aria-hidden="true" />
            </Button>
          ) : null}
        </header>
      ) : (
        <WorkspaceInternalSidebarHeader
          title={t("channels.nav.label")}
          collapsed={collapsed}
          onCollapsedChange={onCollapsedChange}
          expandLabel={t("channels.nav.expandSidebar")}
          collapseLabel={t("channels.nav.collapseSidebar")}
          collapseTestId="channels-sidebar-collapse"
        />
      )}
      <nav
        className={cn("min-h-0 flex-1 overflow-y-auto px-1.5 py-2", isDrawer && "px-3 py-4")}
        aria-label={t("channels.nav.label")}
        data-testid="channels-nav"
      >
        <ul className={cn("m-0 grid list-none p-0", isDrawer ? "gap-1.5" : "gap-0.5")}>
          <li>
            {(() => {
              const allLink = (
                <NavLink
                  to="/channels"
                  end
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    isDrawer
                      ? drawerNavItemClass(isActive)
                      : workspaceInternalNavItemClass(isActive, collapsed)
                  }
                  aria-label={t("channels.nav.all")}
                  data-testid="channels-nav-all"
                >
                  <span className="inline-grid size-7 shrink-0 place-items-center rounded-md bg-surface-muted text-accent">
                    <Radio size={14} aria-hidden="true" />
                  </span>
                  <span className={cn(workspaceInternalNavLabelClass(showLabels), "truncate text-start")}>
                    {t("channels.nav.all")}
                  </span>
                </NavLink>
              );
              return collapsed && !isDrawer ? (
                <SimpleTooltip content={t("channels.nav.all")}>
                  <div className="w-full">{allLink}</div>
                </SimpleTooltip>
              ) : (
                allLink
              );
            })()}
          </li>
          {rows.map((row) => {
            const label = t(`channels.platforms.${row.id}`);
            const status = t(channelStatusLabelKey(row.state));
            const to =
              row.id === "whatsapp"
                ? "/channels/whatsapp"
                : row.id === "facebook"
                  ? "/channels/facebook"
                  : "/channels/instagram";
            const link = (
              <NavLink
                to={to}
                onClick={onNavigate}
                className={({ isActive }) =>
                  isDrawer
                    ? drawerNavItemClass(isActive)
                    : workspaceInternalNavItemClass(isActive, collapsed)
                }
                aria-label={`${label}, ${status}`}
                data-testid={`channels-nav-${row.id}`}
              >
                <ChannelMark platformType={PLATFORM_TYPE[row.id]} size="nav" />
                <span className={cn(workspaceInternalNavLabelClass(showLabels), "grid text-start")}>
                  <span className="truncate">{label}</span>
                  {showLabels ? (
                    <span className="truncate text-[0.7rem] font-normal text-muted">{status}</span>
                  ) : null}
                </span>
              </NavLink>
            );
            return (
              <li key={row.id}>
                {collapsed && !isDrawer ? (
                  <SimpleTooltip content={`${label} · ${status}`}>
                    <div className="w-full">{link}</div>
                  </SimpleTooltip>
                ) : (
                  link
                )}
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
}
